import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Star } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import PlusUpsellModal from '@/components/PlusUpsellModal';
import LoginModal from '@/components/LoginModal';
import {
  getTickers, peekTickers, subscribe, addLocal, removeLocal,
} from '@/lib/portfolioTickersCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Star in the News page's sticky ticker pill (same shared
// portfolioTickersCache as the Founder-Led / IPO Watch stars). Non-
// dividend names show the golden Plus lock for non-admin users — same
// gate as the Dividend / All / Non-Dividend switch — and open the
// Plus upsell instead of starring.
export default function NewsWatchStar({ ticker, isDividend, variant = 'pill' }) {
  const chip = variant === 'chip'; // compact, on card background (strip tiles)
  const table = variant === 'table'; // plain star, matches StockTable / Screener rows
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // PAYWALL FLIP: admin || plus
  const locked = isDividend === false && !isAdmin;

  const initialSet = peekTickers();
  const [inList, setInList] = useState(initialSet ? initialSet.has(ticker) : false);
  const [busy, setBusy] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);

  useEffect(() => {
    if (!user) { setInList(false); return undefined; }
    let cancelled = false;
    getTickers().then((set) => { if (!cancelled) setInList(set.has(ticker)); });
    const unsub = subscribe(() => {
      const set = peekTickers();
      if (set) setInList(set.has(ticker));
    });
    return () => { cancelled = true; unsub(); };
  }, [ticker, user]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (locked) { setPlusOpen(true); return; }
    if (!user) { setLoginOpen(true); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (inList) {
        await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
        removeLocal(ticker);
        setInList(false);
        toast.success(`${ticker} removed from watchlist`);
        window.dispatchEvent(new CustomEvent('watchlist-auto-removed', { detail: { ticker } }));
      } else {
        await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
        addLocal(ticker);
        setInList(true);
        toast.success(`${ticker} added to watchlist`);
        window.dispatchEvent(new CustomEvent('watchlist-auto-added', { detail: { ticker } }));
      }
      window.dispatchEvent(new Event('watchlist-changed'));
      window.dispatchEvent(new Event('portfolio-changed'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(err?.response?.status === 400 && detail ? detail : 'Could not update watchlist');
    } finally {
      setBusy(false);
    }
  };

  const title = locked
    ? 'Non-dividend stocks are a DiviDrip Plus feature — tap to learn more'
    : !user
      ? 'Sign in to add this stock to your watchlist'
      : inList ? `Remove ${ticker} from watchlist` : `Add ${ticker} to watchlist`;

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-pressed={locked ? undefined : inList}
        aria-label={title}
        title={title}
        className={table
          ? 'inline-flex items-center justify-center p-0.5 rounded active:scale-90 transition-[background-color,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-60'
          : chip
          ? 'inline-flex items-center justify-center w-6 h-6 rounded border border-primary/30 bg-primary/5 text-primary hover:bg-primary/15 active:scale-90 transition-[background-color,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-60'
          : 'inline-flex items-center justify-center w-7 h-7 rounded-md text-white/90 hover:bg-white/20 active:scale-90 transition-[background-color,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60'}
        data-testid={locked ? `news-ticker-lock-${ticker}` : `news-ticker-star-${ticker}`}
      >
        {locked ? (
          <Lock className={`${chip || table ? 'w-3.5 h-3.5 text-yellow-500' : 'w-4 h-4 text-yellow-400 drop-shadow'}`} aria-hidden />
        ) : table ? (
          <Star className={`w-4 h-4 transition-colors ${inList ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`} aria-hidden />
        ) : (
          <Star className={`${chip ? 'w-3.5 h-3.5' : 'w-4 h-4'} transition-colors ${inList ? (chip ? 'fill-yellow-400 text-yellow-500' : 'fill-yellow-400 text-yellow-400 drop-shadow') : (chip ? 'text-primary/70' : 'text-white/90')}`} aria-hidden />
        )}
      </button>
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
      <PlusUpsellModal open={plusOpen} onClose={() => setPlusOpen(false)} />
    </>
  );
}
